import React from "react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Link2, ExternalLink, Pencil } from "lucide-react";
import { useItemDetailsContext } from "./ItemDetailsContext";

const ItemDetailsTitleSection = () => {
    const {
        itemType,
        itemDetails,
        href,
        isEditing,
        isLinkEditing,
        folderName,
        linkVal,
        handleNameClick,
        handleNameChange,
        handleNameBlur,
        updateFolderName,
        handleLinkClick,
        handleLinkChange,
        updateLinkValue,
    } = useItemDetailsContext();

    return (
        <div className="space-y-4">
            {/* Name editing */}
            {isEditing ? (
                <div className="space-y-2 animate-in fade-in slide-in-from-top-1 duration-200">
                    <Label htmlFor="folder-name" className="text-xs font-medium text-muted-foreground">
                        Name
                    </Label>
                    <Input
                        id="folder-name"
                        value={folderName}
                        onChange={handleNameChange}
                        onBlur={handleNameBlur}
                        onKeyDown={updateFolderName}
                        autoFocus
                        className="text-lg font-semibold h-12 border-primary/50 focus-visible:ring-primary/20"
                        spellCheck={false}
                    />
                    <p className="text-xs text-muted-foreground">
                        Press Enter to save, Escape to cancel
                    </p>
                </div>
            ) : (
                <div className="group flex items-center gap-2">
                    <h2
                        className={cn(
                            "text-2xl font-bold cursor-pointer transition-colors",
                            "hover:text-primary"
                        )}
                        onClick={handleNameClick}
                    >
                        {itemDetails.name}
                    </h2>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={handleNameClick}
                            >
                                <Pencil className="h-3.5 w-3.5" />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>Edit name</TooltipContent>
                    </Tooltip>
                </div>
            )}

            {/* Link URL editing (only for link type) */}
            {itemType === "link" && (
                <>
                    {isLinkEditing ? (
                        <div className="space-y-2 animate-in fade-in slide-in-from-top-1 duration-200">
                            <Label htmlFor="link-url" className="text-xs font-medium text-muted-foreground">
                                URL
                            </Label>
                            <Input
                                id="link-url"
                                value={linkVal}
                                onChange={handleLinkChange}
                                onKeyDown={updateLinkValue}
                                autoFocus
                                className="font-mono text-sm border-primary/50 focus-visible:ring-primary/20"
                            />
                            <p className="text-xs text-muted-foreground">
                                Press Enter to save
                            </p>
                        </div>
                    ) : (
                        <div className="group flex items-center gap-3 p-2 -mx-2 rounded-lg hover:bg-muted/50 transition-colors">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="secondary"
                                        size="icon"
                                        className="h-8 w-8 shrink-0"
                                        asChild
                                    >
                                        <a
                                            href={href}
                                            rel="noopener noreferrer"
                                            target="_blank"
                                        >
                                            <Link2 className="h-4 w-4" />
                                        </a>
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>Open link</TooltipContent>
                            </Tooltip>
                            <p
                                className="flex-1 text-sm text-muted-foreground cursor-pointer hover:text-foreground transition-colors truncate"
                                onClick={handleLinkClick}
                            >
                                {itemDetails.link}
                            </p>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7"
                                            onClick={handleLinkClick}
                                        >
                                            <Pencil className="h-3.5 w-3.5" />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Edit URL</TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7"
                                            asChild
                                        >
                                            <a
                                                href={href}
                                                rel="noopener noreferrer"
                                                target="_blank"
                                            >
                                                <ExternalLink className="h-3.5 w-3.5" />
                                            </a>
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Open in new tab</TooltipContent>
                                </Tooltip>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ItemDetailsTitleSection;
